import { useEffect, useState, type FC, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Props = {
  /** The element wrapping the editor; its title bar is looked up inside. */
  container: HTMLElement | null;
  children: ReactNode;
};

/**
 * The row holding the document name: the vendor title bar's middle column,
 * first row (the menu bar is the second). Both docx engines lay it out so.
 */
const nameRowIn = (container: HTMLElement): Element | null =>
  container.querySelector('[data-testid="title-bar"]')?.children[1]
    ?.children[0] ?? null;

/**
 * Renders beside the document name, above the menu bar. The editor exposes no
 * slot there, so this owns one node it appends to that row, and appends it
 * again whenever the editor rebuilds the row.
 */
export const TitleBarNote: FC<Props> = ({ container, children }) => {
  const [node] = useState(() => {
    const el = document.createElement("span");
    el.className = "flex min-w-0 items-center";
    el.dataset.testid = "title-bar-note";
    return el;
  });

  useEffect(() => {
    if (!container) return undefined;
    const attach = () => {
      if (node.isConnected && container.contains(node)) return;
      nameRowIn(container)?.appendChild(node);
    };
    attach();
    const observer = new MutationObserver(attach);
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      node.remove();
    };
  }, [container, node]);

  return createPortal(children, node);
};
